/**
 * Text Undo/Redo Utilities
 * Following the same pattern as undoRedoLogo.js but adapted for text functionality
 */
import { redoText, undoText } from '../webGL';

// Text undo/redo stacks (similar to undoLogo/redoLogo)
const MAX_UNDO_STACK_SIZE = 50;
let isUndoRedoActive = false;
// De-duplication of rapid or identical pushes
// Removed last-push de-duplication (rolled back)

// Configuration for significant change detection
const changeThresholds = {
  POSITION_THRESHOLD: 0.1, // Minimum position change to be significant
  ROTATION_THRESHOLD: 0.01, // Minimum rotation change (in radians)
  SCALE_THRESHOLD: 0.01, // Minimum scale change
  TIME_THRESHOLD: 100, // Minimum time between operations (ms)
};

/**
 * Check if we should push to undo queue (without actually pushing)
 */
export function shouldPushToUndoQueue(currentLogo) {
  if (!currentLogo) return false;
  return hasSignificantChanges(currentLogo);
}

export function pushTextUndoQueue(currentLogo = null) {
  if (!currentLogo) {
    return;
  }
  redoText && (redoText.length = 0);
  const textAction = convertTextData({ logo: currentLogo });
  undoText.push(textAction);
  if (undoText.length > MAX_UNDO_STACK_SIZE) {
    undoText.shift();
  }
  updateTexts();
}

function convertTextData(textObj) {
  const logo = textObj.logo;
  const Position = logo?.Position
    ? Object.assign(Object.create(Object.getPrototypeOf(logo.Position)), logo.Position)
    : null;
  const Orientation = logo?.Orientation
    ? Object.assign(Object.create(Object.getPrototypeOf(logo.Orientation)), logo.Orientation)
    : null;

  return {
    type: 'text',
    value: textObj,
    logo: logo,
    Position,
    Orientation,
    text: logo?.Text,
    color: logo?.Color,
    font: logo?.Font,
    rotate: logo?.Rotate,
    scale: logo?.Scale,
    meshId: logo?.meshID,
    isText: logo?.IsText,
    timestamp: Date.now(),
  };
}

function hasSignificantChanges(logo) {
  const lastSnapshot = findLastSnapshotForLogo(logo.Id);
  if (!lastSnapshot) return true;

  const timeSinceLastOperation = Date.now() - (lastSnapshot.timestamp || 0);
  if (timeSinceLastOperation < changeThresholds.TIME_THRESHOLD) return false;

  if (logo.Text !== lastSnapshot.text) return true;
  if (logo.Color !== lastSnapshot.color) return true;
  if (logo.Font !== lastSnapshot.font) return true;
  if (Math.abs((logo.Scale || 1) - (lastSnapshot.scale || 1)) > changeThresholds.SCALE_THRESHOLD)
    return true;
  if (
    Math.abs((logo.Rotate || 0) - (lastSnapshot.rotate || 0)) > changeThresholds.ROTATION_THRESHOLD
  )
    return true;

  if (logo.Position && lastSnapshot.Position) {
    const dx = Math.abs(logo.Position.x - lastSnapshot.Position.x);
    const dy = Math.abs(logo.Position.y - lastSnapshot.Position.y);
    const dz = Math.abs(logo.Position.z - lastSnapshot.Position.z);
    if (
      dx > changeThresholds.POSITION_THRESHOLD ||
      dy > changeThresholds.POSITION_THRESHOLD ||
      dz > changeThresholds.POSITION_THRESHOLD
    ) {
      return true;
    }
  } else if (logo.Position !== lastSnapshot.Position) {
    return true;
  }

  if (logo.Orientation && lastSnapshot.Orientation) {
    const dx = Math.abs(logo.Orientation.x - lastSnapshot.Orientation.x);
    const dy = Math.abs(logo.Orientation.y - lastSnapshot.Orientation.y);
    const dz = Math.abs(logo.Orientation.z - lastSnapshot.Orientation.z);
    if (
      dx > changeThresholds.ROTATION_THRESHOLD ||
      dy > changeThresholds.ROTATION_THRESHOLD ||
      dz > changeThresholds.ROTATION_THRESHOLD
    ) {
      return true;
    }
  } else if (logo.Orientation !== lastSnapshot.Orientation) {
    return true;
  }

  if (logo.meshID !== lastSnapshot.meshId) return true;

  return false;
}

function findLastSnapshotForLogo(logoId) {
  for (let i = undoText.length - 1; i >= 0; i--) {
    if (undoText[i] && undoText[i].length > 0) {
      for (let j = undoText[i].length - 1; j >= 0; j--) {
        if (undoText[i][j].logo && undoText[i][j].logo.Id === logoId) {
          return undoText[i][j];
        }
      }
    }
  }
  return null;
}

let undoTimeout = null;
let isUndoExecuting = false;

export function doUndoText() {
  if (isUndoExecuting) return;
  if (undoTimeout) clearTimeout(undoTimeout);

  undoTimeout = setTimeout(() => {
    isUndoRedoActive = true;
    isUndoExecuting = true;

    if (undoText.length === 0) {
      isUndoExecuting = false;
      isUndoRedoActive = false;
      return;
    }

    if (undoText.length === 1) {
      const lastAction = undoText.pop();
      if (lastAction && lastAction.length > 0) {
        redoText.push(
          lastAction.map((item) => ({
            ...item,
            isDeleted: true,
            timestamp: Date.now(),
          })),
        );
      }
      syncTheTexts([]);
      updateTextButtonStates();
      isUndoExecuting = false;
      isUndoRedoActive = false;
      return;
    }

    const lastAction = undoText.pop();
    if (lastAction) {
      redoText.push(lastAction);

      if (redoText.length > MAX_UNDO_STACK_SIZE) {
        redoText.shift();
      }
    }
    syncTheTexts(undoText[undoText.length - 1] || []);
    updateTextButtonStates();

    setTimeout(() => {
      isUndoExecuting = false;
      isUndoRedoActive = false;
    }, 100);
  }, 50);
}

function syncTheTexts(currentAction) {
  const keptIds = new Set(
    (currentAction || []).map((td) => td && td.logo && td.logo.Id).filter(Boolean),
  );

  [...texts].forEach((textObj) => {
    const id = textObj && textObj.logo && textObj.logo.Id;
    if (!id || !keptIds.has(id)) {
      if (textObj.logo && textObj.logo.IsText === true && textObj.logo.decalMesh && scene) {
        try {
          const mesh = textObj.logo.decalMesh;
          scene.remove(mesh);

          if (mesh.geometry) {
            mesh.geometry.dispose();
          }

          if (mesh.material) {
            if (mesh.material.map) {
              mesh.material.map.dispose();
            }
            mesh.material.dispose();
          }
        } catch (error) {}
      }
    }
  });

  texts.length = 0;

  if (!currentAction || currentAction.length === 0) {
    if (window.clearTextUIState) {
      try {
        window.clearTextUIState();
      } catch (e) {}
    }
    updateTextButtonStates();
    return;
  }

  currentAction.forEach((textData) => {
    if (textData && textData.logo && textData.logo.IsText === true) {
      try {
        if (textData.rotate !== undefined) textData.logo.Rotate = textData.rotate;
        if (textData.scale !== undefined) textData.logo.Scale = textData.scale;
        if (textData.text !== undefined) textData.logo.Text = textData.text;
        if (textData.color !== undefined) textData.logo.Color = textData.color;
        if (textData.font !== undefined) textData.logo.Font = textData.font;
        if (textData.meshId !== undefined) textData.logo.meshID = textData.meshId;
        if (textData.Position) textData.logo.Position = textData.Position;
        if (textData.Orientation) textData.logo.Orientation = textData.Orientation;
      } catch (error) {}

      if (
        window.renderTextOnly &&
        textData.logo.Text &&
        textData.logo.Color &&
        textData.logo.Font
      ) {
        try {
          window.renderTextOnly(textData.logo);
        } catch (error) {}
      }
      try {
        const THREERef = typeof window !== 'undefined' && window.THREE ? window.THREE : null;
        const snapshotPosition = textData.Position;
        const snapshotOrientation = textData.Orientation;

        if (snapshotPosition && snapshotOrientation && scene && THREERef) {
          if (textData.logo.decalMesh && scene) {
            try {
              scene.remove(textData.logo.decalMesh);

              if (textData.logo.decalMesh.geometry) {
                textData.logo.decalMesh.geometry.dispose();
              }

              if (textData.logo.decalMesh.material) {
                const material = textData.logo.decalMesh.material;

                if (material.map) {
                  material.map.dispose();
                }

                if (material !== textData.logo.decalMaterial) {
                  material.dispose();
                }
              }
            } catch (e) {}
          }

          if (!textData.logo.decalMaterial) {
            textData.logo.decalMaterial = new THREERef.MeshPhongMaterial({
              color: textData.color || '#FFFFFF',
              transparent: true,
              opacity: 1,
              depthTest: true,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -4,
              wireframe: false,
            });
          }

          let targetMesh = null;
          if (textData.meshId && scene && scene.traverse) {
            scene.traverse((child) => {
              if (child && child.meshId === textData.meshId) {
                targetMesh = child;
              }
            });
          }

          if (targetMesh) {
            const orientationTemp = new THREERef.Euler();
            orientationTemp.copy(snapshotOrientation);
            orientationTemp.z += ((textData.rotate || 0) * Math.PI) / 180;
            const size = new THREERef.Vector3(
              textData.scale || 1,
              textData.scale || 1,
              textData.scale || 1,
            );
            textData.logo.decalMesh = new THREERef.Mesh(
              new THREERef.DecalGeometry(targetMesh, snapshotPosition, orientationTemp, size),
              textData.logo.decalMaterial,
            );
            textData.logo.decalMesh.logo = textData.logo;
            textData.logo.decalMesh.renderOrder = 1;
            scene.add(textData.logo.decalMesh);
          }
        }
      } catch (e) {}

      const existsIndex = texts.findIndex((t) => t && t.logo && t.logo.Id === textData.logo.Id);
      if (existsIndex === -1) {
        texts.push(textData.logo.decalMesh || textData.value);
      } else if (textData.logo.decalMesh) {
        texts[existsIndex] = textData.logo.decalMesh;
      }
    }
  });

  if (window.texts) {
    window.texts.length = 0;
    texts.forEach((decalMesh) => {
      if (decalMesh && decalMesh.logo) window.texts.push(decalMesh);
    });
  }

  if (window.currentLogo) window.currentLogo = null;

  if (currentAction.length > 0 && window.updateTextUIState) {
    const lastText = currentAction[currentAction.length - 1];
    if (lastText && lastText.logo) window.updateTextUIState(lastText.logo);
  }

  updateTextButtonStates();
}

let redoTimeout = null;
let isRedoExecuting = false;

export function doRedoText() {
  if (isRedoExecuting) return;
  if (redoTimeout) clearTimeout(redoTimeout);

  redoTimeout = setTimeout(() => {
    isUndoRedoActive = true;
    isRedoExecuting = true;

    if (redoText?.length > 0) {
      const lastAction = redoText.pop();
      undoText.push(lastAction);

      if (undoText.length > MAX_UNDO_STACK_SIZE) {
        undoText.shift();
      }

      syncTheTexts(lastAction);
      updateTextButtonStates();
    }

    setTimeout(() => {
      isRedoExecuting = false;
      isUndoRedoActive = false;
    }, 100);
  }, 50);
}

function updateTexts() {
  // This function should update the texts array with current state
  // Implementation depends on how texts are managed in the application
  if (window.texts) {
    texts.length = 0;
    window.texts.forEach((textObj) => {
      if (textObj.logo && textObj.logo.IsText === true) {
        texts.push(textObj);
      }
    });
  }

  // Update button states
  updateTextButtonStates();
}

/**
 * Update button states for text undo/redo
 */
function updateTextButtonStates() {
  // Only update if we're in text mode
  if (window.toolbarActive && window.toolbarActive.value === 'text') {
    if (window.setUndoState) {
      window.setUndoState(undoText.length > 0);
    }
    if (window.setRedoState) {
      window.setRedoState(redoText.length > 0);
    }
  }
}

// Global variables that need to be available
// These should be injected or made available through the application context
let texts = [];
let scene = null;

/**
 * Initialize the text undo/redo system
 * Call this function to set up the required global variables
 */
export function initializeTextUndoRedo(textsArray, sceneObject) {
  texts = textsArray || [];
  scene = sceneObject;
}
